import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdminCompany,
  AdminIndustry,
  AdminSector,
  AdminStats,
  CompensationRecord,
  createCompany,
  createIndustry,
  createSector,
  deleteCompany,
  deleteIndustry,
  deleteRecord,
  deleteSector,
  fetchAdminStats,
  fetchCompanies,
  fetchIndustries,
  fetchRecords,
  fetchSectors,
  formatDate,
  formatYesNo,
  updateCompany,
  updateIndustry,
  updateSector,
} from '../../../services/adminService';
import AdminCompanyPage from './AdminCompanyPage';
import GradingSection from './grading/GradingSection';
import RecordGradingSection from './recordGrading/RecordGradingSection';
import CommunityAdminSection from './community/CommunityAdminSection';
import AdminUserPermissionsSection from './AdminUserPermissionsSection';
import AdminSubscriptionsSection from './AdminSubscriptionsSection';
import { AdminDevModeProvider } from './adminDevMode';
import RecordDrawer from './RecordDrawer';
import RecordExcelUploadModal from './RecordExcelUploadModal';
import {
  AdminBreadcrumbsTw,
  AdminCard,
  AdminDevTh,
  AdminEmpty,
  AdminLayout,
  AdminPageHeader,
  AdminStatCard,
  AdminTableWrap,
  AdminVisibilityField,
  PAGE_TITLES,
  btnLink,
  btnPrimary,
  btnSecondary,
  filtersRowClass,
  inputClass,
  selectClass,
  tdClass,
  textareaClass,
  thClass,
} from './adminUi';
import {
  AdminColumnPicker,
  AdminConfigurableTable,
  AdminColumnDef,
  useAdminColumnVisibility,
} from './adminColumnVisibility';
import { recordCompensationColumns } from './adminRecordColumns';
import { createRecordApprovedColumn } from './RecordApprovedCell';
import {
  AdminField,
  AdminLocation,
  AdminModal,
  AdminNav,
  BreadcrumbItem,
  ConfirmDialog,
  defaultAdminNav,
  isAdminNavAllowed,
} from './adminShared';
import { useAdminPermissions } from './adminPermissions';
import { AdminAnalystPreviewBanner, AdminAnalystPreviewToggle } from './AdminAnalystPreview';

type IndustryForm = { name: string; description: string; is_visible: boolean };
type SectorForm = { industryId: string; name: string; description: string; is_visible: boolean };
type CompanyForm = { industryId: string; sectorId: string; name: string; is_visible: boolean };

const emptyIndustryForm = (): IndustryForm => ({ name: '', description: '', is_visible: true });
const emptySectorForm = (): SectorForm => ({ industryId: '', name: '', description: '', is_visible: true });
const emptyCompanyForm = (): CompanyForm => ({ industryId: '', sectorId: '', name: '', is_visible: true });

const btnDeleteLink = 'text-sm text-red-600 hover:text-red-800 disabled:opacity-50';

function formatVisibilityLabel(isVisible?: boolean) {
  return isVisible === false ? 'Скрыто' : 'Видно';
}

export default function AdminPanel({
  navTarget,
  onNavTargetChange,
}: {
  navTarget?: AdminNav;
  onNavTargetChange?: (nav: AdminNav) => void;
}) {
  const { canManageStructure, canEditRecords, canGradeRecords } = useAdminPermissions();
  const navPermissions = useMemo(
    () => ({ canManageStructure, canEditRecords, canGradeRecords }),
    [canManageStructure, canEditRecords, canGradeRecords],
  );
  const [location, setLocation] = useState<AdminLocation>({ screen: navTarget ?? 'overview' });
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [industries, setIndustries] = useState<AdminIndustry[]>([]);
  const [sectors, setSectors] = useState<AdminSector[]>([]);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [records, setRecords] = useState<CompensationRecord[]>([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsPage, setRecordsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listRefreshing, setListRefreshing] = useState(false);

  const [companySearch, setCompanySearch] = useState('');
  const [recordsSearch, setRecordsSearch] = useState('');
  const [recordsIndustryFilter, setRecordsIndustryFilter] = useState('');
  const [recordsSectorFilter, setRecordsSectorFilter] = useState('');
  const [recordsCompanyFilter, setRecordsCompanyFilter] = useState('');
  const [recordsSort, setRecordsSort] = useState('created_at_desc');
  const [recordsNotValidated, setRecordsNotValidated] = useState(false);

  const [industryModal, setIndustryModal] = useState<{ mode: 'create' | 'edit'; item?: AdminIndustry } | null>(null);
  const [sectorModal, setSectorModal] = useState<{ mode: 'create' | 'edit'; item?: AdminSector } | null>(null);
  const [companyModal, setCompanyModal] = useState<{ mode: 'create' | 'edit'; item?: AdminCompany } | null>(null);
  const [excelUploadOpen, setExcelUploadOpen] = useState(false);
  const [industryForm, setIndustryForm] = useState<IndustryForm>(emptyIndustryForm);
  const [sectorForm, setSectorForm] = useState<SectorForm>(emptySectorForm);
  const [companyForm, setCompanyForm] = useState<CompanyForm>(emptyCompanyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [globalRecordDrawer, setGlobalRecordDrawer] = useState<{ companyId: number; label: string } | null>(null);
  const [editRecordTarget, setEditRecordTarget] = useState<CompensationRecord | null>(null);
  const [deleteRecordTarget, setDeleteRecordTarget] = useState<CompensationRecord | null>(null);
  const [deleteIndustryTarget, setDeleteIndustryTarget] = useState<AdminIndustry | null>(null);
  const [deleteSectorTarget, setDeleteSectorTarget] = useState<AdminSector | null>(null);
  const [deleteCompanyTarget, setDeleteCompanyTarget] = useState<AdminCompany | null>(null);
  const [deleteStructureError, setDeleteStructureError] = useState<string | null>(null);
  const [deletingStructure, setDeletingStructure] = useState(false);
  const skipNavSyncRef = useRef(false);
  const prevNavTargetRef = useRef<AdminNav | undefined>(navTarget);

  const activeNav: AdminNav = useMemo(() => {
    if (location.screen === 'company') return 'companies';
    return location.screen;
  }, [location]);

  const navigate = useCallback((next: AdminLocation) => setLocation(next), []);

  const goNav = (nav: AdminNav) => {
    const target = isAdminNavAllowed(nav, navPermissions) ? nav : defaultAdminNav(navPermissions);
    onNavTargetChange?.(target);
    if (target === 'overview') navigate({ screen: 'overview' });
    if (target === 'industries') navigate({ screen: 'industries' });
    if (target === 'sectors') navigate({ screen: 'sectors' });
    if (target === 'companies') navigate({ screen: 'companies' });
    if (target === 'records') navigate({ screen: 'records' });
    if (target === 'record-grading') navigate({ screen: 'record-grading' });
    if (target === 'grading') navigate({ screen: 'grading' });
    if (target === 'community') navigate({ screen: 'community' });
    if (target === 'permissions') navigate({ screen: 'permissions' });
    if (target === 'subscriptions') navigate({ screen: 'subscriptions' });
  };

  useEffect(() => {
    if (!navTarget) return;

    const prevNavTarget = prevNavTargetRef.current;
    prevNavTargetRef.current = navTarget;

    if (skipNavSyncRef.current) {
      skipNavSyncRef.current = false;
      if (location.screen === 'company') return;
    }
    if (navTarget === 'companies' && location.screen === 'company') return;
    if (location.screen === navTarget) return;

    // Drill-down: location ушёл вглубь, а вкладка сайдбара ещё на родительском уровне
    if (
      location.screen === 'sectors'
      && navTarget === 'industries'
      && prevNavTarget === 'industries'
      && isAdminNavAllowed('sectors', navPermissions)
    ) {
      onNavTargetChange?.('sectors');
      return;
    }
    if (
      location.screen === 'companies'
      && navTarget === 'sectors'
      && prevNavTarget === 'sectors'
      && isAdminNavAllowed('companies', navPermissions)
    ) {
      onNavTargetChange?.('companies');
      return;
    }
    if (
      location.screen === 'companies'
      && navTarget === 'industries'
      && prevNavTarget === 'industries'
      && isAdminNavAllowed('companies', navPermissions)
    ) {
      onNavTargetChange?.('companies');
      return;
    }

    const target = isAdminNavAllowed(navTarget, navPermissions) ? navTarget : defaultAdminNav(navPermissions);
    if (target !== navTarget) onNavTargetChange?.(target);
    if (target === 'overview') navigate({ screen: 'overview' });
    if (target === 'industries') navigate({ screen: 'industries' });
    if (target === 'sectors') navigate({ screen: 'sectors' });
    if (target === 'companies') navigate({ screen: 'companies' });
    if (target === 'records') navigate({ screen: 'records' });
    if (target === 'record-grading') navigate({ screen: 'record-grading' });
    if (target === 'grading') navigate({ screen: 'grading' });
    if (target === 'community') navigate({ screen: 'community' });
    if (target === 'permissions') navigate({ screen: 'permissions' });
    if (target === 'subscriptions') navigate({ screen: 'subscriptions' });
  }, [navTarget, location.screen, navigate, navPermissions, onNavTargetChange]);

  useEffect(() => {
    const screenNav = location.screen === 'company' ? 'companies' : location.screen;
    if (isAdminNavAllowed(screenNav as AdminNav, navPermissions)) return;
    const fallback = defaultAdminNav(navPermissions);
    onNavTargetChange?.(fallback);
    if (fallback === 'permissions') navigate({ screen: 'permissions' });
    else if (fallback === 'record-grading') navigate({ screen: 'record-grading' });
    else if (fallback === 'records') navigate({ screen: 'records' });
    else navigate({ screen: 'overview' });
  }, [navPermissions, location.screen, navigate, onNavTargetChange]);

  const loadOverview = async () => {
    const data = await fetchAdminStats();
    setStats(data);
  };

  const loadIndustries = async () => {
    setIndustries(await fetchIndustries());
  };

  const loadSectors = async (industryId?: number) => {
    setSectors(await fetchSectors(industryId));
  };

  const loadCompanies = async (params?: { industryId?: number; sectorId?: number; search?: string }) => {
    setCompanies(await fetchCompanies(params));
  };

  const loadRecords = async () => {
    const res = await fetchRecords({
      search: recordsSearch.trim() || undefined,
      industryId: recordsIndustryFilter ? Number(recordsIndustryFilter) : undefined,
      sectorId: recordsSectorFilter ? Number(recordsSectorFilter) : undefined,
      companyId: recordsCompanyFilter ? Number(recordsCompanyFilter) : undefined,
      sort: recordsSort,
      page: recordsPage,
      limit: 25,
      notApproved: recordsNotValidated || undefined,
    });
    setRecords(res?.data || []);
    setRecordsTotal(res?.total || 0);
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        if (location.screen === 'overview') await loadOverview();
        if (location.screen === 'industries') await loadIndustries();
        if (location.screen === 'sectors') {
          await loadIndustries();
          await loadSectors(location.industryId);
        }
        if (location.screen === 'companies') {
          await loadIndustries();
          if (location.industryId) await loadSectors(location.industryId);
          await loadCompanies({
            industryId: location.industryId,
            sectorId: location.sectorId,
            search: companySearch.trim() || undefined,
          });
        }
        if (location.screen === 'records') {
          await loadIndustries();
          if (recordsIndustryFilter) await loadSectors(Number(recordsIndustryFilter));
          if (recordsSectorFilter || recordsIndustryFilter) {
            await loadCompanies({
              industryId: recordsIndustryFilter ? Number(recordsIndustryFilter) : undefined,
              sectorId: recordsSectorFilter ? Number(recordsSectorFilter) : undefined,
            });
          }
          await loadRecords();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [location]);

  useEffect(() => {
    if (loading) return;
    if (location.screen !== 'companies' && location.screen !== 'records') return;

    let cancelled = false;
    const run = async () => {
      setListRefreshing(true);
      try {
        if (location.screen === 'companies') {
          await loadCompanies({
            industryId: location.industryId,
            sectorId: location.sectorId,
            search: companySearch.trim() || undefined,
          });
          return;
        }

        if (recordsIndustryFilter) await loadSectors(Number(recordsIndustryFilter));
        if (recordsSectorFilter || recordsIndustryFilter) {
          await loadCompanies({
            industryId: recordsIndustryFilter ? Number(recordsIndustryFilter) : undefined,
            sectorId: recordsSectorFilter ? Number(recordsSectorFilter) : undefined,
          });
        }
        await loadRecords();
      } finally {
        if (!cancelled) setListRefreshing(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [
    companySearch,
    recordsSearch,
    recordsIndustryFilter,
    recordsSectorFilter,
    recordsCompanyFilter,
    recordsSort,
    recordsPage,
    recordsNotValidated,
  ]);

  const openCompany = (company: AdminCompany, opts?: { quickAdd?: boolean; openRecord?: boolean }) => {
    skipNavSyncRef.current = true;
    onNavTargetChange?.('companies');
    navigate({
      screen: 'company',
      companyId: company.id,
      industryId: company.industry_id,
      sectorId: company.sector_id,
      quickAdd: opts?.quickAdd,
      openRecord: opts?.openRecord,
    });
  };

  const companyFromRecord = (record: CompensationRecord): AdminCompany => ({
    id: record.company_id,
    name: record.company_name || '',
    industry_id: record.industry_id || 0,
    industry_name: record.industry_name || '',
    sector_id: record.sector_id || 0,
    sector_name: record.sector_name || '',
    records_count: 0,
    last_record_at: null,
    updated_at: '',
  });

  const recordContextLabel = (record: CompensationRecord) =>
    [record.industry_name, record.sector_name, record.company_name].filter(Boolean).join(' → ');

  const handleRecordApproved = useCallback((updated: CompensationRecord) => {
    setRecords((prev) => {
      if (recordsNotValidated) {
        return prev.filter((r) => r.id !== updated.id);
      }
      return prev.map((r) => (r.id === updated.id ? { ...r, is_approved: true } : r));
    });
    if (recordsNotValidated) {
      setRecordsTotal((total) => Math.max(0, total - 1));
    }
  }, [recordsNotValidated]);

  const recordsColumns = useMemo((): AdminColumnDef<CompensationRecord>[] => [
    {
      id: 'industry',
      label: 'Индустрия',
      dbField: 'industries.name',
      render: (record) => record.industry_name || '—',
    },
    {
      id: 'sector',
      label: 'Сектор',
      dbField: 'sectors.name',
      render: (record) => record.sector_name || '—',
    },
    {
      id: 'company',
      label: 'Компания',
      dbField: 'companies.name',
      render: (record) => (
        <button type="button" className={btnLink} onClick={() => openCompany(companyFromRecord(record))}>
          {record.company_name}
        </button>
      ),
    },
    {
      id: 'last_name',
      label: 'Фамилия',
      dbField: 'compensation_records.last_name',
      render: (record) => record.last_name || '—',
    },
    {
      id: 'first_name',
      label: 'Имя',
      dbField: 'compensation_records.first_name',
      render: (record) => record.first_name || '—',
    },
    {
      id: 'middle_name',
      label: 'Отчество',
      dbField: 'compensation_records.middle_name',
      render: (record) => record.middle_name || '—',
    },
    {
      id: 'role_name',
      label: 'Должность',
      dbField: 'compensation_records.company_role_name',
      render: (record) => record.company_role_name,
    },
    {
      id: 'grade',
      label: 'Грейд',
      dbField: 'compensation_records.grade_level',
      render: (record) => record.grade_level ?? '—',
    },
    {
      id: 'grading_role',
      label: 'Роль',
      dbField: 'roles.name',
      render: (record) => record.role_name || '—',
    },
    {
      id: 'period',
      label: 'Период',
      dbField: 'compensation_records.added_date',
      render: (record) => formatDate(record.added_date),
    },
    ...recordCompensationColumns,
    {
      id: 'source',
      label: 'Источник',
      dbField: 'compensation_records.source',
      render: (record) => record.source,
    },
    {
      id: 'created',
      label: 'Добавлено',
      dbField: 'compensation_records.created_at',
      render: (record) => formatDate(record.created_at),
    },
    createRecordApprovedColumn(handleRecordApproved),
    {
      id: 'role_validated',
      label: 'Грейд подтвержден',
      dbField: 'compensation_records.is_role_validated',
      render: (record) => formatYesNo(record.is_role_validated),
    },
    {
      id: 'actions',
      label: 'Действия',
      hideable: false,
      cellClassName: `${tdClass} whitespace-nowrap`,
      render: (record) => (
        <>
          <button type="button" className={btnLink} onClick={() => setEditRecordTarget(record)} disabled={!canEditRecords}>Редактировать</button>
          {canManageStructure && (
            <>
              {' · '}
              <button type="button" className="text-sm font-medium text-red-600 hover:underline" onClick={() => setDeleteRecordTarget(record)}>Удалить</button>
            </>
          )}
        </>
      ),
    },
  ], [canManageStructure, canEditRecords, handleRecordApproved]);

  const recordsColVis = useAdminColumnVisibility('employee-records', recordsColumns);

  const breadcrumbsBase: BreadcrumbItem[] = [{ label: 'Админ-панель', onClick: () => goNav('overview') }];

  const saveIndustry = async () => {
    setSaving(true);
    setFormError(null);
    try {
      if (industryModal?.mode === 'edit' && industryModal.item) {
        await updateIndustry(industryModal.item.id, industryForm);
      } else {
        const created = await createIndustry(industryForm.name.trim(), industryForm.description.trim() || undefined);
        if (!industryForm.is_visible && created?.id) {
          await updateIndustry(created.id, { is_visible: false });
        }
      }
      setIndustryModal(null);
      await loadIndustries();
      if (location.screen === 'overview') await loadOverview();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const saveSector = async () => {
    setSaving(true);
    setFormError(null);
    try {
      if (!sectorForm.industryId && sectorModal?.mode === 'create') throw new Error('Выберите индустрию');
      if (sectorModal?.mode === 'edit' && sectorModal.item) {
        await updateSector(sectorModal.item.id, {
          name: sectorForm.name.trim(),
          description: sectorForm.description.trim() || undefined,
          is_visible: sectorForm.is_visible,
        });
      } else {
        const created = await createSector(Number(sectorForm.industryId), sectorForm.name.trim(), sectorForm.description.trim() || undefined);
        if (!sectorForm.is_visible && created?.id) {
          await updateSector(created.id, { is_visible: false });
        }
      }
      setSectorModal(null);
      await loadSectors(location.screen === 'sectors' && 'industryId' in location ? location.industryId : undefined);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const saveCompany = async () => {
    setSaving(true);
    setFormError(null);
    try {
      if (!companyForm.industryId || !companyForm.sectorId) throw new Error('Выберите индустрию и сектор');
      if (companyModal?.mode === 'edit' && companyModal.item) {
        await updateCompany(companyModal.item.id, { name: companyForm.name.trim(), is_visible: companyForm.is_visible });
      } else {
        const created = await createCompany(Number(companyForm.industryId), Number(companyForm.sectorId), companyForm.name.trim());
        if (!companyForm.is_visible && created?.id) {
          await updateCompany(created.id, { is_visible: false });
        }
      }
      setCompanyModal(null);
      await loadCompanies({
        industryId: location.screen === 'companies' && location.industryId ? location.industryId : undefined,
        sectorId: location.screen === 'companies' && location.sectorId ? location.sectorId : undefined,
        search: companySearch.trim() || undefined,
      });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteIndustry = async () => {
    if (!deleteIndustryTarget) return;
    setDeletingStructure(true);
    setDeleteStructureError(null);
    try {
      await deleteIndustry(deleteIndustryTarget.id);
      const deletedId = deleteIndustryTarget.id;
      setDeleteIndustryTarget(null);
      if (location.screen === 'sectors' && location.industryId === deletedId) {
        navigate({ screen: 'industries' });
      }
      if (location.screen === 'companies' && location.industryId === deletedId) {
        navigate({ screen: 'industries' });
      }
      await loadIndustries();
      if (location.screen === 'overview') await loadOverview();
    } catch (error) {
      setDeleteStructureError(error instanceof Error ? error.message : 'Не удалось удалить индустрию');
    } finally {
      setDeletingStructure(false);
    }
  };

  const confirmDeleteSector = async () => {
    if (!deleteSectorTarget) return;
    setDeletingStructure(true);
    setDeleteStructureError(null);
    try {
      await deleteSector(deleteSectorTarget.id);
      const deletedId = deleteSectorTarget.id;
      const industryId = deleteSectorTarget.industry_id;
      setDeleteSectorTarget(null);
      if (location.screen === 'companies' && location.sectorId === deletedId) {
        navigate({ screen: 'companies', industryId });
      }
      await loadSectors(location.screen === 'sectors' && 'industryId' in location ? location.industryId : undefined);
      await loadIndustries();
      if (location.screen === 'overview') await loadOverview();
    } catch (error) {
      setDeleteStructureError(error instanceof Error ? error.message : 'Не удалось удалить сектор');
    } finally {
      setDeletingStructure(false);
    }
  };

  const confirmDeleteCompany = async () => {
    if (!deleteCompanyTarget) return;
    setDeletingStructure(true);
    setDeleteStructureError(null);
    const target = deleteCompanyTarget;
    try {
      await deleteCompany(target.id);
      setDeleteCompanyTarget(null);
      if (location.screen === 'company' && location.companyId === target.id) {
        navigate({
          screen: 'companies',
          industryId: target.industry_id,
          sectorId: target.sector_id,
        });
      }
      await loadCompanies({
        industryId: location.screen === 'companies' && location.industryId ? location.industryId : undefined,
        sectorId: location.screen === 'companies' && location.sectorId ? location.sectorId : undefined,
        search: companySearch.trim() || undefined,
      });
      await loadIndustries();
      if (location.screen === 'overview') await loadOverview();
    } catch (error) {
      setDeleteStructureError(error instanceof Error ? error.message : 'Не удалось удалить компанию');
    } finally {
      setDeletingStructure(false);
    }
  };

  const companyFormSectors = useMemo(
    () => sectors.filter((s) => String(s.industry_id) === companyForm.industryId),
    [sectors, companyForm.industryId],
  );

  const renderRefreshingTable = (children: React.ReactNode) => (
    <AdminTableWrap refreshing={listRefreshing}>{children}</AdminTableWrap>
  );

  const renderOverview = () => (
    <>
      <AdminBreadcrumbsTw items={breadcrumbsBase} />
      <AdminPageHeader title="Обзор" subtitle="Структура данных и быстрый вход в рабочие сценарии" />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <AdminStatCard label="Индустрий" value={stats?.industries_count ?? '—'} />
        <AdminStatCard label="Секторов" value={stats?.sectors_count ?? '—'} />
        <AdminStatCard label="Компаний" value={stats?.companies_count ?? '—'} />
        <AdminStatCard label="Записей сотрудников" value={stats?.records_count ?? '—'} />
      </div>

      <AdminCard className="mb-5">
        <h2 className="mb-3 text-base font-semibold text-slate-900">Быстрые действия</h2>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btnPrimary} onClick={() => goNav('companies')}>Добавить запись о сотруднике</button>
          {canManageStructure && (
            <>
              <button type="button" className={btnSecondary} onClick={() => { setIndustryForm(emptyIndustryForm()); setIndustryModal({ mode: 'create' }); }}>Добавить индустрию</button>
              <button type="button" className={btnSecondary} onClick={() => { setSectorForm(emptySectorForm()); setSectorModal({ mode: 'create' }); }}>Добавить сектор</button>
              <button type="button" className={btnSecondary} onClick={() => { setCompanyForm(emptyCompanyForm()); setCompanyModal({ mode: 'create' }); loadIndustries(); }}>Добавить компанию</button>
            </>
          )}
        </div>
      </AdminCard>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <AdminCard>
          <h2 className="mb-3 text-base font-semibold text-slate-900">Последние компании</h2>
          <AdminTableWrap>
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50/80"><tr><th className={thClass}>Компания</th><th className={thClass}>Записей</th><th className={thClass}></th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(stats?.recentCompanies || []).map((c) => (
                  <tr key={c.id}><td className={tdClass}>{c.name}</td><td className={tdClass}>{c.records_count}</td><td className={tdClass}><button type="button" className={btnLink} onClick={() => openCompany(c as AdminCompany)}>Открыть</button></td></tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        </AdminCard>
        <AdminCard>
          <h2 className="mb-3 text-base font-semibold text-slate-900">Последние записи</h2>
          <AdminTableWrap>
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50/80"><tr><th className={thClass}>Должность</th><th className={thClass}>Компания</th><th className={thClass}>Период</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(stats?.recentRecords || []).map((r) => (
                  <tr key={r.id}><td className={tdClass}>{r.company_role_name}</td><td className={tdClass}>{r.company_name}</td><td className={tdClass}>{formatDate(r.added_date)}</td></tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        </AdminCard>
      </div>
    </>
  );

  const renderIndustries = () => (
    <>
      <AdminBreadcrumbsTw items={[...breadcrumbsBase, { label: 'Индустрии' }]} />
      <AdminPageHeader
        title="Индустрии"
        subtitle="Редкое управление верхним уровнем иерархии"
        actions={canManageStructure ? (
          <button type="button" className={btnSecondary} onClick={() => { setIndustryForm(emptyIndustryForm()); setIndustryModal({ mode: 'create' }); }}>Добавить индустрию</button>
        ) : undefined}
      />
      <AdminTableWrap>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50/80">
            <tr>
              <AdminDevTh label="Название" dbField="industries.name" />
              <AdminDevTh label="Секторов" dbField="sectors_count" />
              <AdminDevTh label="Компаний" dbField="companies_count" />
              <AdminDevTh label="Записей" dbField="records_count" />
              <AdminDevTh label="Видимость" dbField="industries.is_visible" />
              <AdminDevTh label="Изменено" dbField="industries.updated_at" />
              <AdminDevTh label="Действия" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {industries.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50/50">
                <td className={tdClass}><button type="button" className={btnLink} onClick={() => navigate({ screen: 'sectors', industryId: item.id })}>{item.name}</button></td>
                <td className={tdClass}>{item.sectors_count}</td>
                <td className={tdClass}>{item.companies_count}</td>
                <td className={tdClass}>{item.records_count}</td>
                <td className={tdClass}>{formatVisibilityLabel(item.is_visible)}</td>
                <td className={tdClass}>{formatDate(item.updated_at)}</td>
                <td className={`${tdClass} whitespace-nowrap`}>
                  <button type="button" className={btnLink} onClick={() => navigate({ screen: 'sectors', industryId: item.id })}>Открыть</button>
                  {canManageStructure && (
                    <>
                      {' · '}
                      <button type="button" className={btnLink} onClick={() => { setIndustryForm({ name: item.name, description: item.description || '', is_visible: item.is_visible !== false }); setIndustryModal({ mode: 'edit', item }); }}>Редактировать</button>
                      {' · '}
                      <button
                        type="button"
                        className={btnDeleteLink}
                        onClick={() => { setDeleteStructureError(null); setDeleteIndustryTarget(item); }}
                      >
                        Удалить
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableWrap>
    </>
  );

  const renderSectors = () => {
    const filteredIndustryId = location.screen === 'sectors' ? location.industryId : undefined;
    const crumbs = [...breadcrumbsBase];
    const currentIndustry = industries.find((i) => i.id === filteredIndustryId);
    if (currentIndustry) crumbs.push({ label: currentIndustry.name, onClick: () => navigate({ screen: 'sectors', industryId: currentIndustry.id }) });
    crumbs.push({ label: 'Сектора' });

    return (
      <>
        <AdminBreadcrumbsTw items={crumbs} />
        <AdminPageHeader
          title="Сектора"
          subtitle="Управление секторами внутри индустрий"
          actions={canManageStructure ? (
            <button type="button" className={btnSecondary} onClick={() => { setSectorForm({ ...emptySectorForm(), industryId: filteredIndustryId ? String(filteredIndustryId) : '' }); setSectorModal({ mode: 'create' }); }}>Добавить сектор</button>
          ) : undefined}
        />
        <div className={filtersRowClass}>
          <select className={selectClass} value={filteredIndustryId ? String(filteredIndustryId) : ''} onChange={(e) => navigate({ screen: 'sectors', industryId: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">Все индустрии</option>
            {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </div>
        <AdminTableWrap>
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/80">
              <tr>
                <AdminDevTh label="Сектор" dbField="sectors.name" />
                <AdminDevTh label="Индустрия" dbField="industries.name" />
                <AdminDevTh label="Компаний" dbField="companies_count" />
                <AdminDevTh label="Записей" dbField="records_count" />
                <AdminDevTh label="Видимость" dbField="sectors.is_visible" />
                <AdminDevTh label="Изменено" dbField="sectors.updated_at" />
                <AdminDevTh label="Действия" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sectors.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/50">
                  <td className={tdClass}><button type="button" className={btnLink} onClick={() => navigate({ screen: 'companies', industryId: item.industry_id, sectorId: item.id })}>{item.name}</button></td>
                  <td className={tdClass}>{item.industry_name}</td>
                  <td className={tdClass}>{item.companies_count}</td>
                  <td className={tdClass}>{item.records_count}</td>
                  <td className={tdClass}>{formatVisibilityLabel(item.is_visible)}</td>
                  <td className={tdClass}>{formatDate(item.updated_at)}</td>
                  <td className={`${tdClass} whitespace-nowrap`}>
                    <button type="button" className={btnLink} onClick={() => navigate({ screen: 'companies', industryId: item.industry_id, sectorId: item.id })}>Открыть</button>
                    {canManageStructure && (
                      <>
                        {' · '}
                        <button type="button" className={btnLink} onClick={() => { setSectorForm({ industryId: String(item.industry_id), name: item.name, description: item.description || '', is_visible: item.is_visible !== false }); setSectorModal({ mode: 'edit', item }); }}>Редактировать</button>
                        {' · '}
                        <button
                          type="button"
                          className={btnDeleteLink}
                          onClick={() => { setDeleteStructureError(null); setDeleteSectorTarget(item); }}
                        >
                          Удалить
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableWrap>
      </>
    );
  };

  const renderCompanies = () => {
    const filteredIndustryId = location.screen === 'companies' ? location.industryId : undefined;
    const filteredSectorId = location.screen === 'companies' ? location.sectorId : undefined;
    const currentIndustry = industries.find((i) => String(i.id) === String(filteredIndustryId));
    const currentSector = sectors.find((s) => String(s.id) === String(filteredSectorId));
    const crumbs = [...breadcrumbsBase];
    if (currentIndustry) crumbs.push({ label: currentIndustry.name, onClick: () => navigate({ screen: 'companies', industryId: currentIndustry.id }) });
    if (currentSector) crumbs.push({ label: currentSector.name, onClick: () => navigate({ screen: 'companies', industryId: currentIndustry?.id, sectorId: currentSector.id }) });
    crumbs.push({ label: 'Компании' });

    return (
      <>
        <AdminBreadcrumbsTw items={crumbs} />
        <AdminPageHeader
          title="Компании"
          subtitle="Выберите компанию для массового добавления записей"
          actions={canManageStructure ? (
            <button type="button" className={btnSecondary} onClick={() => { setCompanyForm({ industryId: filteredIndustryId ? String(filteredIndustryId) : '', sectorId: filteredSectorId ? String(filteredSectorId) : '', name: '', is_visible: true }); setCompanyModal({ mode: 'create' }); loadSectors(filteredIndustryId); }}>Добавить компанию</button>
          ) : undefined}
        />
        <div className={filtersRowClass}>
          <select className={selectClass} value={filteredIndustryId ? String(filteredIndustryId) : ''} onChange={(e) => navigate({ screen: 'companies', industryId: e.target.value ? Number(e.target.value) : undefined, sectorId: undefined })}>
            <option value="">Все индустрии</option>
            {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <select className={selectClass} value={filteredSectorId ? String(filteredSectorId) : ''} onChange={(e) => navigate({ screen: 'companies', industryId: filteredIndustryId, sectorId: e.target.value ? Number(e.target.value) : undefined })} disabled={!filteredIndustryId}>
            <option value="">Все сектора</option>
            {sectors.filter((s) => !filteredIndustryId || String(s.industry_id) === String(filteredIndustryId)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input className={inputClass} placeholder="Поиск по названию" value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} />
        </div>
        {renderRefreshingTable(
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/80">
              <tr>
                <AdminDevTh label="Компания" dbField="companies.name" />
                <AdminDevTh label="Индустрия" dbField="industries.name" />
                <AdminDevTh label="Сектор" dbField="sectors.name" />
                <AdminDevTh label="Записей" dbField="records_count" />
                <AdminDevTh label="Видимость" dbField="companies.is_visible" />
                <AdminDevTh label="Последняя запись" dbField="last_record_at" />
                <AdminDevTh label="Действия" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {companies.map((item) => (
                <tr key={`${item.id}-${item.sector_id}`} className="hover:bg-slate-50/50">
                  <td className={tdClass}><button type="button" className={btnLink} onClick={() => openCompany(item)}>{item.name}</button></td>
                  <td className={tdClass}>{item.industry_name}</td>
                  <td className={tdClass}>{item.sector_name}</td>
                  <td className={tdClass}>{item.records_count}</td>
                  <td className={tdClass}>{formatVisibilityLabel(item.is_visible)}</td>
                  <td className={tdClass}>{formatDate(item.last_record_at)}</td>
                  <td className={`${tdClass} whitespace-nowrap`}>
                    <button type="button" className={btnLink} onClick={() => openCompany(item)}>Открыть</button>
                    {' · '}
                    <button type="button" className={btnLink} onClick={() => openCompany(item, { openRecord: true })}>Добавить сотрудника</button>
                    {canManageStructure && (
                      <>
                        {' · '}
                        <button type="button" className={btnLink} onClick={() => { setCompanyForm({ industryId: String(item.industry_id), sectorId: String(item.sector_id), name: item.name, is_visible: item.is_visible !== false }); setCompanyModal({ mode: 'edit', item }); loadSectors(item.industry_id); }}>Редактировать</button>
                        {' · '}
                        <button
                          type="button"
                          className={btnDeleteLink}
                          onClick={() => { setDeleteStructureError(null); setDeleteCompanyTarget(item); }}
                        >
                          Удалить
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>,
        )}
      </>
    );
  };

  const renderRecords = () => (
    <>
      <AdminBreadcrumbsTw items={[...breadcrumbsBase, { label: 'Записи сотрудников' }]} />
      <AdminPageHeader
        title="Записи сотрудников"
        subtitle="Глобальный список всех записей по компаниям"
        actions={(
          <button type="button" className={btnSecondary} onClick={() => setExcelUploadOpen(true)}>
            <i className="fas fa-file-excel mr-2 text-emerald-600" aria-hidden="true" />
            Загрузить Excel файл
          </button>
        )}
      />
      <div className={filtersRowClass}>
        <input className={inputClass} placeholder="Глобальный поиск" value={recordsSearch} onChange={(e) => { setRecordsSearch(e.target.value); setRecordsPage(1); }} />
        <select className={selectClass} value={recordsIndustryFilter} onChange={(e) => { setRecordsIndustryFilter(e.target.value); setRecordsSectorFilter(''); setRecordsCompanyFilter(''); setRecordsPage(1); }}>
          <option value="">Все индустрии</option>
          {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select className={selectClass} value={recordsSectorFilter} onChange={(e) => { setRecordsSectorFilter(e.target.value); setRecordsCompanyFilter(''); setRecordsPage(1); }} disabled={!recordsIndustryFilter}>
          <option value="">Все сектора</option>
          {sectors.filter((s) => String(s.industry_id) === recordsIndustryFilter).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select className={selectClass} value={recordsCompanyFilter} onChange={(e) => { setRecordsCompanyFilter(e.target.value); setRecordsPage(1); }}>
          <option value="">Все компании</option>
          {companies.map((c) => <option key={`${c.id}-${c.sector_id}`} value={c.id}>{c.name}</option>)}
        </select>
        <select className={selectClass} value={recordsSort} onChange={(e) => setRecordsSort(e.target.value)}>
          <option value="created_at_desc">Сначала новые</option>
          <option value="added_date_desc">По периоду</option>
        </select>
        <button
          type="button"
          className={`${btnSecondary} whitespace-nowrap ${recordsNotValidated ? '!border-slate-900 !bg-slate-900 !text-white' : ''}`}
          onClick={() => { setRecordsNotValidated((v) => !v); setRecordsPage(1); }}
        >
          Не валидированы
        </button>
        <AdminColumnPicker
          hideableColumns={recordsColVis.hideableColumns}
          hiddenIds={recordsColVis.hiddenIds}
          onToggle={recordsColVis.toggleColumn}
          onShowAll={recordsColVis.showAllColumns}
        />
      </div>
      {renderRefreshingTable(
        <AdminConfigurableTable
          columns={recordsColVis.visibleColumns}
          rows={records}
          rowKey={(record) => record.id}
          minWidth={1180}
        />,
      )}
      {recordsTotal > 25 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-dc-muted">
          <span>Всего: {recordsTotal}</span>
          <div className="flex gap-2">
            <button type="button" className={btnSecondary} disabled={recordsPage <= 1} onClick={() => setRecordsPage((p) => p - 1)}>Назад</button>
            <button type="button" className={btnSecondary} disabled={recordsPage * 25 >= recordsTotal} onClick={() => setRecordsPage((p) => p + 1)}>Вперёд</button>
          </div>
        </div>
      )}
    </>
  );

  const renderContent = () => {
    if (location.screen === 'company') {
      return (
        <AdminCompanyPage
          companyId={location.companyId}
          sectorId={location.sectorId}
          quickAdd={location.quickAdd}
          openRecordOnLoad={location.openRecord}
          onNavigate={navigate}
          onBackToCompanies={() => {
            if (location.industryId && location.sectorId) {
              navigate({ screen: 'companies', industryId: location.industryId, sectorId: location.sectorId });
            } else {
              goNav('companies');
            }
          }}
        />
      );
    }
    if (location.screen === 'grading') return <GradingSection />;
    if (location.screen === 'community') return <CommunityAdminSection />;
    if (location.screen === 'permissions') return <AdminUserPermissionsSection />;
    if (location.screen === 'subscriptions') return <AdminSubscriptionsSection />;
    if (location.screen === 'record-grading') return <RecordGradingSection />;
    if (loading) return <AdminEmpty>Загрузка…</AdminEmpty>;
    if (location.screen === 'overview') return renderOverview();
    if (location.screen === 'industries') return renderIndustries();
    if (location.screen === 'sectors') return renderSectors();
    if (location.screen === 'companies') return renderCompanies();
    return renderRecords();
  };

  const pageTitle = location.screen === 'company' ? 'Компании' : PAGE_TITLES[activeNav];

  return (
    <AdminDevModeProvider>
    <AdminLayout
      pageTitle={pageTitle}
      headerActions={<AdminAnalystPreviewToggle />}
    >
      <AdminAnalystPreviewBanner />
      {renderContent()}

      {excelUploadOpen && (
        <RecordExcelUploadModal
          industries={industries}
          canEditRecords={canEditRecords}
          onImported={async () => {
            await loadRecords();
            if (location.screen === 'overview') await loadOverview();
          }}
          onClose={() => setExcelUploadOpen(false)}
        />
      )}

      {industryModal && (
        <AdminModal
          title={industryModal.mode === 'edit' ? 'Редактировать индустрию' : 'Добавить индустрию'}
          onClose={() => setIndustryModal(null)}
          footer={
            <>
              <button type="button" className={btnSecondary} onClick={() => setIndustryModal(null)}>Отмена</button>
              <button type="button" className={btnPrimary} onClick={saveIndustry} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</button>
            </>
          }
        >
          {formError && <p className="mb-3 text-sm text-red-600">{formError}</p>}
          <div className="space-y-4">
            <AdminField label="Название" required><input className={inputClass} value={industryForm.name} onChange={(e) => setIndustryForm((p) => ({ ...p, name: e.target.value }))} /></AdminField>
            <AdminField label="Описание"><textarea className={textareaClass} rows={3} value={industryForm.description} onChange={(e) => setIndustryForm((p) => ({ ...p, description: e.target.value }))} /></AdminField>
            <AdminVisibilityField
              checked={industryForm.is_visible}
              onChange={(is_visible) => setIndustryForm((p) => ({ ...p, is_visible }))}
              dbField="industries.is_visible"
            />
          </div>
        </AdminModal>
      )}

      {sectorModal && (
        <AdminModal
          title={sectorModal.mode === 'edit' ? 'Редактировать сектор' : 'Добавить сектор'}
          onClose={() => setSectorModal(null)}
          footer={
            <>
              <button type="button" className={btnSecondary} onClick={() => setSectorModal(null)}>Отмена</button>
              <button type="button" className={btnPrimary} onClick={saveSector} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</button>
            </>
          }
        >
          {formError && <p className="mb-3 text-sm text-red-600">{formError}</p>}
          <div className="space-y-4">
            <AdminField label="Индустрия" required>
              <select className={`${selectClass} dc-select-full`} value={sectorForm.industryId} onChange={(e) => setSectorForm((p) => ({ ...p, industryId: e.target.value }))} disabled={sectorModal.mode === 'edit'}>
                <option value="">Выберите индустрию</option>
                {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </AdminField>
            <AdminField label="Название сектора" required><input className={inputClass} value={sectorForm.name} onChange={(e) => setSectorForm((p) => ({ ...p, name: e.target.value }))} /></AdminField>
            <AdminField label="Описание"><textarea className={textareaClass} rows={3} value={sectorForm.description} onChange={(e) => setSectorForm((p) => ({ ...p, description: e.target.value }))} /></AdminField>
            <AdminVisibilityField
              checked={sectorForm.is_visible}
              onChange={(is_visible) => setSectorForm((p) => ({ ...p, is_visible }))}
              dbField="sectors.is_visible"
            />
          </div>
        </AdminModal>
      )}

      {companyModal && (
        <AdminModal
          title={companyModal.mode === 'edit' ? 'Редактировать компанию' : 'Добавить компанию'}
          onClose={() => setCompanyModal(null)}
          footer={
            <>
              <button type="button" className={btnSecondary} onClick={() => setCompanyModal(null)}>Отмена</button>
              <button type="button" className={btnPrimary} onClick={saveCompany} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</button>
            </>
          }
        >
          {formError && <p className="mb-3 text-sm text-red-600">{formError}</p>}
          <div className="space-y-4">
            <AdminField label="Индустрия" required>
              <select
                className={`${selectClass} dc-select-full`}
                value={companyForm.industryId}
                onChange={(e) => {
                  const nextIndustry = e.target.value;
                  if (companyForm.sectorId && nextIndustry !== companyForm.industryId) {
                    const ok = window.confirm('Смена индустрии сбросит выбранный сектор. Продолжить?');
                    if (!ok) return;
                  }
                  setCompanyForm((p) => ({ ...p, industryId: nextIndustry, sectorId: '' }));
                  if (nextIndustry) loadSectors(Number(nextIndustry));
                }}
                disabled={companyModal.mode === 'edit'}
              >
                <option value="">Выберите индустрию</option>
                {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </AdminField>
            <AdminField label="Сектор" required>
              <select className={`${selectClass} dc-select-full`} value={companyForm.sectorId} onChange={(e) => setCompanyForm((p) => ({ ...p, sectorId: e.target.value }))} disabled={!companyForm.industryId || companyModal.mode === 'edit'}>
                <option value="">Выберите сектор</option>
                {companyFormSectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </AdminField>
            <AdminField label="Название компании" required><input className={inputClass} value={companyForm.name} onChange={(e) => setCompanyForm((p) => ({ ...p, name: e.target.value }))} /></AdminField>
            <AdminVisibilityField
              checked={companyForm.is_visible}
              onChange={(is_visible) => setCompanyForm((p) => ({ ...p, is_visible }))}
              dbField="companies.is_visible"
            />
          </div>
        </AdminModal>
      )}

      {globalRecordDrawer && (
        <RecordDrawer
          open
          mode="create"
          companyId={globalRecordDrawer.companyId}
          contextLabel={globalRecordDrawer.label}
          onClose={() => setGlobalRecordDrawer(null)}
          onSaved={() => setGlobalRecordDrawer(null)}
        />
      )}

      {editRecordTarget && (
        <RecordDrawer
          open
          mode="edit"
          companyId={editRecordTarget.company_id}
          contextLabel={recordContextLabel(editRecordTarget)}
          record={editRecordTarget}
          onClose={() => setEditRecordTarget(null)}
          onSaved={() => {
            setEditRecordTarget(null);
            loadRecords();
          }}
        />
      )}

      {deleteRecordTarget && (
        <ConfirmDialog
          title="Удалить запись?"
          message={`Запись «${deleteRecordTarget.company_role_name}» будет удалена.`}
          confirmLabel="Удалить"
          danger
          onCancel={() => setDeleteRecordTarget(null)}
          onConfirm={async () => {
            await deleteRecord(deleteRecordTarget.id);
            setDeleteRecordTarget(null);
            await loadRecords();
          }}
        />
      )}

      {deleteIndustryTarget && (
        <ConfirmDialog
          title="Удалить индустрию?"
          message={(
            <>
              <span>Индустрия «{deleteIndustryTarget.name}» будет удалена без возможности восстановления.</span>
              {(deleteIndustryTarget.sectors_count > 0 || deleteIndustryTarget.records_count > 0) && (
                <span className="mt-2 block text-red-700">
                  {deleteIndustryTarget.sectors_count > 0 && `Секторов: ${deleteIndustryTarget.sectors_count}. `}
                  {deleteIndustryTarget.records_count > 0 && `Записей: ${deleteIndustryTarget.records_count}.`}
                  {' '}Сначала удалите дочерние объекты.
                </span>
              )}
              {deleteStructureError && <span className="mt-2 block text-red-700">{deleteStructureError}</span>}
            </>
          )}
          confirmLabel={deletingStructure ? 'Удаление…' : 'Удалить'}
          danger
          confirmDisabled={deletingStructure}
          onCancel={() => { setDeleteIndustryTarget(null); setDeleteStructureError(null); }}
          onConfirm={confirmDeleteIndustry}
        />
      )}

      {deleteSectorTarget && (
        <ConfirmDialog
          title="Удалить сектор?"
          message={(
            <>
              <span>Сектор «{deleteSectorTarget.name}» ({deleteSectorTarget.industry_name}) будет удалён.</span>
              {(deleteSectorTarget.companies_count > 0 || deleteSectorTarget.records_count > 0) && (
                <span className="mt-2 block text-red-700">
                  {deleteSectorTarget.companies_count > 0 && `Компаний: ${deleteSectorTarget.companies_count}. `}
                  {deleteSectorTarget.records_count > 0 && `Записей: ${deleteSectorTarget.records_count}.`}
                  {' '}Сначала удалите дочерние объекты.
                </span>
              )}
              {deleteStructureError && <span className="mt-2 block text-red-700">{deleteStructureError}</span>}
            </>
          )}
          confirmLabel={deletingStructure ? 'Удаление…' : 'Удалить'}
          danger
          confirmDisabled={deletingStructure}
          onCancel={() => { setDeleteSectorTarget(null); setDeleteStructureError(null); }}
          onConfirm={confirmDeleteSector}
        />
      )}

      {deleteCompanyTarget && (
        <ConfirmDialog
          title="Удалить компанию?"
          message={(
            <>
              <span>
                Компания «{deleteCompanyTarget.name}» ({deleteCompanyTarget.industry_name} → {deleteCompanyTarget.sector_name}) будет удалена.
              </span>
              {deleteCompanyTarget.records_count > 0 && (
                <span className="mt-2 block text-red-700">
                  Записей сотрудников: {deleteCompanyTarget.records_count}. Сначала удалите записи.
                </span>
              )}
              {deleteStructureError && <span className="mt-2 block text-red-700">{deleteStructureError}</span>}
            </>
          )}
          confirmLabel={deletingStructure ? 'Удаление…' : 'Удалить'}
          danger
          confirmDisabled={deletingStructure}
          onCancel={() => { setDeleteCompanyTarget(null); setDeleteStructureError(null); }}
          onConfirm={confirmDeleteCompany}
        />
      )}
    </AdminLayout>
    </AdminDevModeProvider>
  );
}
